import { HeatsTeamItem } from '../models/teams';
import { RoundItem } from '../models/rounds';
import { ResultItem, RoundStateItem } from '../models/results';
import { JudgeItem } from '../models/judges';
import { JudgingType, JudgeType } from '@vote4dance/judging-schema';
import { HeatsTeamId } from '@vote4dance/judging-types';

export type ResultRenderComponent = {
    id: string;
    name?: any;
    step?: number;
};

export type RoundStateRenderModelParams = {
    showTrainees?: boolean;
    preview?: boolean;
    includeResultComments?: boolean;
    includePartialRows?: boolean;
    components?: ResultRenderComponent[];
    summaryField?: 'sum' | 'placement';
    decimalStep?: number;
    filteredCoachTeams?: string[];
    coachFilter?: RoundStateCoachFilterParams;
    hideMarks?: boolean;
};

export type RoundStatePartialDataParams = Pick<
    RoundItem,
    'judging_type' | 'judging_components' | 'round_children' | 'round_hide_results_pos' | 'round_hide_results_type'
>;

export type RoundStateSummaryData = {
    sums?: Array<any>;
    groupSums?: Array<any>;
};

export type RoundStatePlacementMeta = {
    priorityType?: string | null;
    priority?: number | string | null;
};

export type RoundStatePlacementLabelInfo =
    | { type: 'alternates-level'; priority: number }
    | { type: 'alternates-through' }
    | { type: 'localized-key'; key: string; params?: Record<string, unknown> }
    | { type: 'raw'; value: unknown };

export type RoundStatePartialDetailModel = {
    components: Array<ResultRenderComponent & { isPartialArray?: boolean }>;
    judges: any[];
    isComponentRating: boolean;
    isPercentRating: boolean;
    usesStars: boolean;
};

export type RoundStateRenderModel = {
    judges: string[];
    rows: any[];
    summaryField: 'sum' | 'placement';
    filteredCoachTeams?: string[];
};

export type RoundStateCoachFilterParams = {
    checkins?: Array<{
        user_id?: string | number | null;
        organization_name?: string | null;
    }>;
    sessionUserId?: string | number | null;
    userCompetitionLevel?: string | number | null;
};

export type RoundStateSummaryChildState = {
    round_id?: string | number | null;
    round_parent_id?: string | number | null;
    state_data?: {
        teams?: Array<string | number>;
        sums?: Array<any>;
    } | null;
};

export type CollectStarsSummaryRowParams = {
    row: Record<string, any>;
    teamId: string | number;
    teamIndex: number;
    judgeIds: Array<string | number>;
    stateData?: {
        sums?: Array<any>;
        teams?: Array<string | number>;
        componentSums?: Array<any>;
    };
    childStates?: RoundStateSummaryChildState[];
    componentSums?: Record<string, any> | null;
};

export class RoundStateHelper {
    private _stateData : any;
    private _teams : HeatsTeamItem[] = [];
    private _round : RoundItem;
    private _ratings : ResultItem[] = [];
    private _judges : JudgeItem[] = [];
    private _hideMarks = false;
    private _partialResults: ResultItem[] = [];
    private _resultComments: ResultItem[] = [];
    private _classRounds: RoundItem[] = [];
    private _states: RoundStateItem[] = [];

    async initialize(stateData: any, teams: HeatsTeamItem[], round: RoundItem, ratings: ResultItem[], judges: JudgeItem[], hideMarks: boolean, partialResults: ResultItem[], resultComments: ResultItem[], classRounds: RoundItem[], states: RoundStateItem[]) {
        this._stateData = stateData;
        this._teams = teams;
        this._round = round;
        this._ratings = ratings;
        this._judges = judges;
        this._hideMarks = hideMarks;
        this._partialResults = partialResults;
        this._resultComments = resultComments;
        this._classRounds = classRounds;
        this._states = states;
    }

    static safeParseJson<T>(value?: string | null): T | undefined {
        if (!value) {
            return undefined;
        }
        try {
            return JSON.parse(value) as T;
        } catch {
            return undefined;
        }
    }

    private static getNumberOfDecimals(step: number) {
        if (!Number.isFinite(step)) {
            return 0;
        }
        const stepAsString = step.toString();
        if (stepAsString.includes('e-')) {
            const exponent = stepAsString.split('e-')[1];
            return exponent ? parseInt(exponent, 10) : 0;
        }
        const decimalPart = stepAsString.split('.')[1];
        return decimalPart ? decimalPart.length : 0;
    }

    static normalizeRemarkState(remark?: unknown) {
        if (typeof remark === 'string') {
            return remark.toLowerCase().trim();
        }
        if (Array.isArray(remark)) {
            const firstString = remark.find(value => typeof value === 'string' && value.trim().length > 0);
            return firstString ? firstString.toLowerCase().trim() : '';
        }
        return '';
    }

    static getRemarkStateInfo(remark?: unknown) {
        const state = RoundStateHelper.normalizeRemarkState(remark);
        if (!state) {
            return null;
        }
        return {
            state,
            labelKey: `remark.state.${state}`,
            hintKey: `remark.state.${state}-hint`
        };
    }

    static shouldHidePartialInputComponents(round: Pick<RoundItem, 'judging_type' | 'round_hide_results_pos' | 'round_hide_results_type'>) {
        const hidePosition = round.round_hide_results_pos;
        const hideType = round.round_hide_results_type;
        return (
            round.judging_type == JudgingType.PartialInput &&
            hideType == 'partial' &&
            (hidePosition == 0 || hidePosition == 1)
        );
    }

    static shouldUsePartialData(round: RoundStatePartialDataParams, preview = false) {
        if (!preview && RoundStateHelper.shouldHidePartialInputComponents(round)) {
            return false;
        }
        switch (round.judging_type) {
            case JudgingType.Ajs:
            case JudgingType.Partial:
            case JudgingType.PartialInput:
                return true;
            case JudgingType.Match:
                return !!round.judging_components;
            case JudgingType.CollectStars:
                return preview && round.round_children == 0;
            default:
                return false;
        }
    }

    static getDisplayComponents(round: Pick<RoundItem, 'judging_components' | 'judging_settings'>): ResultRenderComponent[] {
        const parsedComponents = RoundStateHelper.safeParseJson<{ components?: ResultRenderComponent[] }>(round.judging_components);
        if (!parsedComponents?.components) {
            return [];
        }
        const baseComponents = [...parsedComponents.components];
        const judgingSettings = RoundItem.getJudgingSettings(round as RoundItem) as {
            acrobatics?: boolean;
            remarks?: boolean;
        };
        if (judgingSettings.acrobatics) {
            baseComponents.push({ id: 'acrobatics', step: 0.1 });
        }
        if (judgingSettings.remarks) {
            baseComponents.push({ id: 'remarks', step: 1 });
        }
        return baseComponents;
    }

    static isAggregateJudgeId(judge_id: string) {
        return ['base', 'requalify', 'partial', 'multiplier', 'average'].includes(judge_id);
    }

    static getAggregateJudgeLabelKey(judge_id: string) {
        if (!RoundStateHelper.isAggregateJudgeId(judge_id)) {
            return null;
        }
        return `round.state.${judge_id}`;
    }

    static getPlacementLabelInfo(
        placementLabel: unknown,
        placementMeta?: RoundStatePlacementMeta,
        hasPriorities = false
    ): RoundStatePlacementLabelInfo {
        if (
            hasPriorities &&
            placementMeta?.priorityType === 'alternates' &&
            placementMeta.priority != null
        ) {
            const priority = Number(placementMeta.priority);
            if (priority > 0) {
                return { type: 'alternates-level', priority };
            }
            return { type: 'alternates-through' };
        }
        if (typeof placementLabel === 'string' && placementLabel.startsWith('team.')) {
            return {
                type: 'localized-key',
                key: placementLabel,
                params: { priority: placementMeta?.priority }
            };
        }
        return { type: 'raw', value: placementLabel ?? '' };
    }

    static buildPartialDetailModel(params: {
        round: Pick<RoundItem, 'judging_type' | 'judging_components' | 'round_children'> & {
            judging_acro?: number;
        };
        item: {
            id?: string | number;
            judges?: any[];
        };
        partialResults?: Array<{
            heats_team_id?: string | number;
            judge_id?: string | number;
            result_partial?: string | null;
        }> | null;
    }): RoundStatePartialDetailModel {
        const { round, item, partialResults } = params;
        const isComponentRating = round.judging_type === JudgingType.Ajs;
        const isPercentRating = round.judging_type === JudgingType.Match && !!round.judging_components;
        const usesStars = RoundItem.usesStars(round as RoundItem);

        if (!isComponentRating && (!partialResults || partialResults.length === 0)) {
            return {
                components: [],
                judges: [],
                isComponentRating,
                isPercentRating,
                usesStars
            };
        }

        const parsedComponents = RoundStateHelper.safeParseJson<{ components?: ResultRenderComponent[] }>(
            typeof round.judging_components === 'string' ? round.judging_components : undefined
        );
        const parsedComponentList = parsedComponents?.components;
        const baseComponents = Array.isArray(parsedComponentList)
            ? [...parsedComponentList]
            : [];

        const teamPartial = (partialResults || []).filter(result => String(result?.heats_team_id) === String(item?.id));

        let validAcrobatics = 0;
        let validRemarks = 0;
        const judges = (item?.judges || []).map((judge: any) => {
            if (isComponentRating) {
                const partialFromJudge = judge?.partial;
                if (partialFromJudge && typeof partialFromJudge === 'object') {
                    return {
                        ...judge,
                        partial: partialFromJudge
                    };
                }
                return {
                    ...judge,
                    partial: RoundStateHelper.safeParseJson<Record<string, unknown>>(
                        typeof judge?.rating === 'string' ? judge.rating : undefined
                    ) || {}
                };
            }

            const judgePartial = teamPartial.find(result => String(result?.judge_id) === String(judge?.id));
            if (!judgePartial?.result_partial) {
                return judge;
            }

            const parsedObject = RoundStateHelper.safeParseJson<Record<string, any>>(judgePartial.result_partial);
            if (!parsedObject) {
                return judge;
            }

            validRemarks += Array.isArray(parsedObject.remarks) ? parsedObject.remarks.length : 0;
            validAcrobatics += Array.isArray(parsedObject.acrobatics) ? parsedObject.acrobatics.length : 0;
            return {
                ...judge,
                partial: parsedObject
            };
        });

        const addComponent = (id: string, extra: Record<string, unknown> = {}) => {
            if (baseComponents.some(component => component.id === id || component.name === id)) {
                return;
            }
            baseComponents.push({ id, name: id, ...extra });
        };

        if ((round.judging_acro || 0) > 0 && validAcrobatics > 0) {
            addComponent('acrobatics', { isPartialArray: true });
        }
        if (
            (round.judging_type == JudgingType.Partial || round.judging_type == JudgingType.PartialInput) &&
            round.round_children == 0 &&
            validRemarks > 0
        ) {
            addComponent('remarks', { isPartialArray: true });
        }
        if (!usesStars) {
            addComponent('summary');
        }

        return {
            components: baseComponents,
            judges,
            isComponentRating,
            isPercentRating,
            usesStars
        };
    }

    static shouldShowSumColumn(round: RoundStatePartialDataParams, stateData: RoundStateSummaryData | undefined, preview = false) {
        return !!(
            stateData?.sums && stateData.sums.some((sum) => sum !== undefined && sum !== null) ||
            stateData?.groupSums && stateData.groupSums.some((sum) => sum !== undefined && sum !== null) ||
            RoundStateHelper.shouldUsePartialData(round, preview)
        );
    }

    static mergeCollectStarsSummaryRow(params: CollectStarsSummaryRowParams) {
        const { row, teamId, teamIndex, judgeIds, stateData, childStates, componentSums } = params;
        if (!row || !Array.isArray(judgeIds)) {
            return row;
        }

        const stateSums = stateData?.sums;
        if (teamIndex >= 0 && Array.isArray(stateSums)) {
            const stateSum = stateSums[teamIndex];
            if (stateSum != null) {
                row.sum = stateSum;
            }
        }

        const teamComponentSums = (componentSums ?? stateData?.componentSums?.[teamIndex]) as any;
        const teamComponentSumKeys =
            teamComponentSums && typeof teamComponentSums === 'object'
                ? Object.keys(teamComponentSums)
                : [];

        if (Array.isArray(childStates) && childStates.length > 0) {
            judgeIds.forEach((judgeId, judgeIndex) => {
                const valueKey = `result_${judgeId}`;
                const currentValue = row[valueKey];
                if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                    return;
                }

                const matchedChildState =
                    childStates.find(child => String(child.round_id) === String(judgeId)) ||
                    childStates[judgeIndex];
                const matchedChildData = matchedChildState?.state_data;
                if (!matchedChildData?.teams || !Array.isArray(matchedChildData.sums)) {
                    return;
                }

                const matchedChildTeamIndex = matchedChildData.teams.findIndex(
                    (id) => String(id) === String(teamId)
                );
                const fallbackChildTeamIndex =
                    matchedChildTeamIndex >= 0
                        ? matchedChildTeamIndex
                        : (
                            teamIndex >= 0 &&
                            teamIndex < matchedChildData.sums.length
                                ? teamIndex
                                : -1
                        );
                if (fallbackChildTeamIndex < 0) {
                    return;
                }

                const matchedChildSum = matchedChildData.sums[fallbackChildTeamIndex];
                if (matchedChildSum != null) {
                    row[valueKey] = matchedChildSum;
                }
            });
        }

        judgeIds.forEach((judgeId, judgeIndex) => {
            const valueKey = `result_${judgeId}`;
            const currentValue = row[valueKey];
            if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                return;
            }

            let componentValue = teamComponentSums?.[String(judgeId)];
            if (
                componentValue == null &&
                teamComponentSums &&
                teamComponentSumKeys.length === judgeIds.length
            ) {
                const fallbackKey = teamComponentSumKeys[judgeIndex];
                componentValue = fallbackKey ? teamComponentSums[fallbackKey] : null;
            }

            let sumValue: unknown = componentValue;
            if (componentValue && typeof componentValue === 'object') {
                const typed = componentValue as { s?: unknown; m?: unknown; value?: unknown; summary?: unknown };
                sumValue = typed.s ?? typed.m ?? typed.value ?? typed.summary;
            }

            if (sumValue !== undefined && sumValue !== null && sumValue !== '') {
                row[valueKey] = sumValue;
            }
        });

        return row;
    }

    shouldShowSumColumn(preview = false, stateData: RoundStateSummaryData | undefined = this._stateData) {
        return RoundStateHelper.shouldShowSumColumn(this._round, stateData, preview);
    }

    getSummaryField(preview = false, stateData: RoundStateSummaryData | undefined = this._stateData): 'sum' | 'placement' {
        return this.shouldShowSumColumn(preview, stateData) ? 'sum' : 'placement';
    }

    getPlacementLabelInfo(placementLabel: unknown, placementMeta?: RoundStatePlacementMeta) {
        return RoundStateHelper.getPlacementLabelInfo(placementLabel, placementMeta, !!this._stateData?.priorities);
    }

    getFilteredCoachTeams(params: RoundStateCoachFilterParams = {}) {
        if (!RoundItem.filteredCoach(this._round)) {
            return undefined;
        }

        const isCompetitionAdmin = Number(params.userCompetitionLevel || 0) >= 100;
        const sessionUserId = params.sessionUserId;
        const checkins = Array.isArray(params.checkins) ? params.checkins : [];
        const userCheckins = checkins.filter(report => String(report?.user_id) === String(sessionUserId));
        const coachOrganizations = userCheckins
            .map(checkin => String(checkin?.organization_name || ''))
            .filter(Boolean);

        if (!isCompetitionAdmin && coachOrganizations.length === 0) {
            return [];
        }

        return (this._teams || [])
            .filter(team => {
                if (isCompetitionAdmin) {
                    return true;
                }
                const organizations = String((team as any)?.team_org || '').split('/');
                return coachOrganizations.some(org => organizations.includes(org));
            })
            .map(team => String((team as any).heats_team_id));
    }

    buildTeamList(filteredCoachTeams?: string[]) {
        const hidePartialInputComponents = this.shouldHidePartialInputComponents();
        const teamList = this._stateData.teams.map((team: string, teamIndex: number) => {
            const placement = this.getTeamPlacement(teamIndex);
            const teamObject: any = {};
            teamObject.id = team;
            teamObject.startNumber = this._stateData.teamNames[team];
            teamObject.fullName = this.getFullNameWithOrg(team);
            teamObject.hasRatings = this._stateData.ratings && this._stateData.ratings.length > 0;
            teamObject.judges = this.getJudges().map((judge: any) => {
                return {
                    id: judge,
                    fullname: this.getJudgeFullName(judge),
                    letter: this.getJudgeName(judge),
                    score: this._stateData.scores ? this.getJudgeScores(judge, teamIndex, placement, filteredCoachTeams) : [],
                    partial: this.getJudgePartial(judge, teamIndex, placement, filteredCoachTeams),
                    comments: this.getJudgeComments(judge, team),
                    remarks: this.getJudgeRemarks(judge, teamIndex, placement, filteredCoachTeams),
                    rating: this.getJudgeRatings(judge, teamIndex, placement, filteredCoachTeams)
                };
            });
            teamObject.majority = this._stateData.majority ? this._stateData.majority[teamIndex] : [];
            teamObject.counts = this._stateData.counts ? this._stateData.counts[teamIndex] : [];
            teamObject.sums = this.getTeamSumLabel(teamIndex, filteredCoachTeams);
            teamObject.groupSums = this._stateData.groupSums ? this._stateData.groupSums[teamIndex] : [];
            teamObject.componentSums = this.getTeamComponentSumLabel(teamIndex);
            teamObject.remarks = this._stateData.remarks ? this._stateData.remarks[teamIndex] : [];
            teamObject.placementLabel = this._stateData.priorities ? this.getTeamPriorityLabel(team, teamIndex) : this.getTeamPlacementLabel(teamIndex);
            teamObject.priority = this._stateData.priorities && this._stateData.priorities[team];
            teamObject.placement = placement;
            teamObject.shouldShowPartial = !hidePartialInputComponents && (this.hasRoundJudgeIdValue() || RoundItem.shouldShowPartial(this._round, placement, team as any, filteredCoachTeams as any));
            teamObject.userModified = this._teams.find(a => a.heats_team_id == team) ? (this._teams.find(a => a.heats_team_id == team) as any).heats_team_target_usermodified : 0;
            return teamObject;
        }, this);

        const highestPriority = this._stateData.priorities ? Math.max(...Object.values(this._stateData.priorities).map((value: any) => Number(value ?? 0))) : 0;
        const sortValue = (team: any) => {
            if (RoundItem.isPointSummary(this._round)) {
                return -(team.sums || 0);
            }
            if (RoundItem.usesPlacement(this._round) && RoundItem.shouldShowPlacement(this._round, team.placement, team.id, filteredCoachTeams as any)) {
                if (this._round.round_num_through == 0) {
                    return team.placement;
                }
                if (RoundItem.usesPriority(this._round)) {
                    const sortingValue = team.priority == null ? (highestPriority + 1) : team.priority;
                    return sortingValue + team.placement;
                }
            }
            return team.startNumber;
        };

        return teamList.sort((n1: any, n2: any) => {
            const first = sortValue(n1);
            const second = sortValue(n2);
            if (first < second) {
                return -1;
            }
            if (first > second) {
                return 1;
            }
            return 0;
        });
    }


    sortDisplayRows(rows: any[], filteredCoachTeams?: string[], hideMarks?: boolean) {
        const highestPriority = this._stateData.priorities
            ? Math.max(...Object.values(this._stateData.priorities).map((value: any) => Number(value ?? 0)))
            : 0;

        const startNumberSortValue = (item: any) => {
            if (
                item.startNumber !== undefined &&
                item.startNumber !== null &&
                item.startNumber !== '' &&
                !Number.isNaN(Number(item.startNumber))
            ) {
                return Number(item.startNumber);
            }
            return item.startNumber || item.teamName || '';
        };

        const compareSortValues = (a: string | number, b: string | number) => {
            if (typeof a === 'string' && typeof b === 'string') {
                return a.localeCompare(b);
            }
            if (a < b) {
                return -1;
            }
            if (a > b) {
                return 1;
            }
            return 0;
        };

        const sortKey = (item: any) => {
            if (RoundItem.isPointSummary(this._round)) {
                return -(item.sum ?? 0);
            }

            if (!hideMarks && RoundItem.usesPlacement(this._round) && RoundItem.shouldShowPlacement(this._round, item.position, item.teamId as any, filteredCoachTeams as any)) {
                const placementValue = Number.isNaN(Number(item.position)) ? Number.POSITIVE_INFINITY : Number(item.position);
                if (this._round.round_num_through == 0 || !RoundItem.usesPriority(this._round)) {
                    return placementValue;
                }
                const priorityValue = item.priority != null ? item.priority : highestPriority + 1;
                return priorityValue + placementValue;
            }

            return startNumberSortValue(item);
        };

        return [...rows].sort((a: any, b: any) => {
            const keyA = sortKey(a);
            const keyB = sortKey(b);
            return compareSortValues(keyA, keyB);
        });
    }

    shouldHidePartialInputComponents() {
        return RoundStateHelper.shouldHidePartialInputComponents(this._round);
    }

    shouldUseNdPartialJudgeMarkVisibility() {
        const federationTag = String((this._round as any)?.competition_federation || '');
        return federationTag.startsWith('ND');
    }

    shouldShowJudgeMarksValue(placement: number, team?: HeatsTeamId, filteredCoachTeams?: string[]) {
        const canShowMarks = RoundItem.shouldShowMarks(this._round, placement, team, filteredCoachTeams as any);
        if (!canShowMarks) {
            return false;
        }
        if (!this.shouldUseNdPartialJudgeMarkVisibility()) {
            return true;
        }
        return RoundItem.shouldShowPartial(this._round, placement, team, filteredCoachTeams as any);
    }

    getTeamPlacementMeta(team: string, teamIndex: number) {
        const roundPrioritySettings = RoundItem.getPrioritySettings(this._round) as any;
        const placementLabel = this.getTeamPlacementLabel(teamIndex);
        const priority = this._stateData.priorities ? this._stateData.priorities[team] : null;
        return {
            placement: this.getTeamPlacement(teamIndex),
            placementLabel,
            priority,
            priorityType: roundPrioritySettings?.type || null,
            priorityLabel: this._stateData.priorities ? this.getTeamPriorityLabel(team, teamIndex) : placementLabel
        };
    }

    getFullNameWithOrg(team: string) {
        const teamHeat = this._teams.find(heatsteam => heatsteam.heats_team_id == team);
        if(teamHeat) {
            return HeatsTeamItem.getFullNameWithOrg(teamHeat);
        } else {
            return '';
        }
    }

    getJudges(trainees?: any) {
        if (RoundItem.isPointSummary(this._round)) {
            const hasRequalify = this._stateData.componentSums?.some((item: any) => {
                return item && Object.keys(item).includes('requalify');
            });
            const newJudges = [...(this._stateData.judges || [])].filter((judge: any) => {
                return hasRequalify ? true : judge !== 'requalify';
            });
            newJudges.push('partial');
            return newJudges;
        }

        if (!this.hasRoundJudgeIdValue() && RoundItem.usesStars(this._round) && this._round.round_children == 0) {
            return ['average'];
        }

        let judges = this._stateData.judges;
        if (this._ratings && (trainees === undefined || !!trainees)) {
            if (this.hasTraineeJudge()) {
                judges = [this._round.judge_id!].concat(judges);
            }
        }
        return judges;
    }

    getJudgeName(judge_id: string) {
        const aggregateLabelKey = RoundStateHelper.getAggregateJudgeLabelKey(judge_id);
        if (aggregateLabelKey) {
            return aggregateLabelKey;
        }

        if (this._ratings && this.isTraineeJudgeId(judge_id)) {
            return 'X';
        }
        return this._stateData.judgeNames[judge_id];
    }

    getJudgeFullName(judge_id: string) {
        if (judge_id == 'average') {
            return 'round.stars.average';
        }
        if (this.isTraineeJudgeId(judge_id)) {
            return 'judges.name.trainee';
        }
        const foundJudge = this._judges.find(a => a.judge_id == judge_id);
        if(foundJudge) {
            return JudgeItem.getFullName(foundJudge);
        } else {
            return '';
        }
    }

    getJudgeScores(judge_id: string, teamIndex: number, placement: number, filteredCoachTeams?: string[]) {
        const team = this._stateData.teams[teamIndex];
        if (!this.hasRoundJudgeIdValue() && (this._hideMarks || !this.shouldShowJudgeMarksValue(placement, team, filteredCoachTeams))) {
            return null;
        }
        if (this._ratings && this.isTraineeJudgeId(judge_id)) {
            const score = this._ratings.find(result => result.heats_team_id == this._stateData.teams[teamIndex]);
            return score && score.result_rating ? score.result_rating : '-';
        }
        return this._stateData.scores?.[judge_id]?.[teamIndex];
    }

    getJudgePartial(judge_id: string, teamIndex: number, placement: number, filteredCoachTeams?: string[]) {
        if (judge_id == 'average') {
            return null;
        }

        const team = this._stateData.teams[teamIndex];
        if (!this.hasRoundJudgeIdValue() && (this._hideMarks || !RoundItem.shouldShowPartial(this._round, placement, team, filteredCoachTeams as any))) {
            return null;
        }

        let partialTeam;
        if (this._ratings && this.isTraineeJudgeId(judge_id)) {
            partialTeam = this._ratings.find(result => result.heats_team_id == team);
            return partialTeam ? (RoundStateHelper.safeParseJson(partialTeam.result_partial) || null) : null;
        }

        if (this._round.judging_type == JudgingType.Ajs) {
            const rating = this._stateData.ratings?.[judge_id]?.[teamIndex];
            if (!rating) {
                return null;
            }
            const judgeResults = RoundStateHelper.safeParseJson<Record<string, any>>(rating);
            if (!judgeResults) {
                return null;
            }
            const mappings = Object.keys(judgeResults);
            mappings.map(mapping => {
                const value = judgeResults[mapping];
                const median = this._stateData.componentSums[teamIndex][mapping].m;
                const tolerance = this._stateData.tolerance;
                const topLimitTolerance = median + tolerance;
                const bottomLimitTolerance = median - tolerance;
                const isOutsideMedian = (value < bottomLimitTolerance) || (value > topLimitTolerance);
                judgeResults[mapping] = {
                    isOutsideMedian,
                    value
                };
            });
            return judgeResults;
        }

        partialTeam = this._partialResults.find(result => result.judge_id == judge_id && result.heats_team_id == team);
        return partialTeam ? (RoundStateHelper.safeParseJson(partialTeam.result_partial) || null) : null;
    }

    getJudgeComments(judge_id: string, team: string) {
        if (this._ratings && this.isTraineeJudgeId(judge_id)) {
            const foundTeam = this._ratings.find(result => result.heats_team_id == team);
            return foundTeam ? (RoundStateHelper.safeParseJson(foundTeam.result_comment) || null) : null;
        }
        const foundTeam = this._resultComments.find(result => result.judge_id == judge_id && result.heats_team_id == team);
        return foundTeam ? (RoundStateHelper.safeParseJson(foundTeam.result_comment) || null) : null;
    }

    getJudgeRatings(judge_id: string, teamIndex: number, placement: number, filteredCoachTeams?: string[]) {
        if (['average', 'partial'].includes(judge_id)) {
            return null;
        }

        const team = this._stateData.teams[teamIndex];
        if (!this.hasRoundJudgeIdValue() && (!this._stateData.ratings || this._hideMarks || !this.shouldShowJudgeMarksValue(placement, team, filteredCoachTeams))) {
            return null;
        }

        if (this._ratings && this.isTraineeJudgeId(judge_id)) {
            const score = this._ratings.find(result => result.heats_team_id == this._stateData.teams[teamIndex]);
            switch (this._round.judging_type) {
                case JudgingType.Select:
                case JudgingType.Match: {
                    return score && parseInt(score.result_rating!) == 1 ? 'x' : '';
                } break;

                case JudgingType.OneToFive:
                case JudgingType.Placement: {
                    return score && score.result_rating ? score.result_rating : '-';
                } break;

                case JudgingType.Ajs:
                case JudgingType.Full:
                case JudgingType.Partial:
                case JudgingType.PartialInput: {
                    return '-';
                } break;
            }
        }
        return this._stateData.ratings?.[judge_id]?.[teamIndex];
    }

    getJudgeDisplayResult(
        judge_id: string,
        teamIndex: number,
        options?: {
            preview?: boolean;
            filteredCoachTeams?: string[];
            decimalStep?: number;
        }
    ) {
        const placement = this.getTeamPlacement(teamIndex);
        const score = this.getJudgeScores(judge_id, teamIndex, placement, options?.filteredCoachTeams) || '';
        let rating = this.getJudgeRatings(judge_id, teamIndex, placement, options?.filteredCoachTeams) || '';

        switch (this._round.judging_type) {
            case JudgingType.Ajs: {
                const resultData = this.getJudgePartial(judge_id, teamIndex, placement, options?.filteredCoachTeams);
                const decimals = RoundStateHelper.getNumberOfDecimals(options?.decimalStep || 0.1);
                if (resultData && typeof resultData === 'object') {
                    rating = Object.values(resultData).reduce((accumulator: number, currentValue: any) => {
                        const value = typeof currentValue === 'number' ? currentValue : (currentValue?.value || 0);
                        return accumulator + value;
                    }, 0).toFixed(decimals);
                } else if (typeof rating === 'string' && rating) {
                    const parsedRating = RoundStateHelper.safeParseJson<Record<string, unknown>>(rating);
                    if (!parsedRating) {
                        rating = '';
                    } else {
                        rating = Object.values(parsedRating).reduce((accumulator: number, currentValue: any) => {
                            const value = typeof currentValue === 'number' ? currentValue : 0;
                            return accumulator + value;
                        }, 0).toFixed(decimals);
                    }
                }
            } break;
            default: {
                if (RoundItem.isPointSummary(this._round)) {
                    rating = this._stateData.componentSums?.[teamIndex]?.[judge_id]?.s ?? '';
                }
                if (score) {
                    rating = `${score} / #${rating}`;
                }
            } break;
        }

        const team = this._stateData.teams?.[teamIndex];
        const canShowMarks = options?.preview || (!this._hideMarks && this.shouldShowJudgeMarksValue(placement, team, options?.filteredCoachTeams));
        return canShowMarks ? rating : '';
    }

    buildComponentJudgeResults(params: {
        teamIndex: number;
        componentId: string;
        judges: string[];
        preview?: boolean;
        summaryField: 'sum' | 'placement';
        decimalStep?: number;
    }) {
        const { teamIndex, componentId, judges, preview, summaryField, decimalStep } = params;
        const placement = this.getTeamPlacement(teamIndex);
        const rowData: Record<string, any> = {};

        if (this._round.judging_type == JudgingType.Ajs) {
            judges.forEach(judge_id => {
                const resultData = this.getJudgePartial(judge_id, teamIndex, placement) as Record<string, any> | null;
                if (!resultData) {
                    return;
                }
                if (resultData[componentId] !== undefined) {
                    rowData[`result_${judge_id}`] = resultData[componentId];
                }
            });
        }

        const stateSums = this._stateData.componentSums?.[teamIndex];
        const stateKeys = stateSums && Object.keys(stateSums);
        if (stateKeys && stateKeys.length > 0) {
            if (RoundItem.isPointSummary(this._round)) {
                const allSums = stateKeys.map((key: string) => stateSums[key].s);
                const totalSum = allSums.reduce((accumulator: number, currentValue: number) => (accumulator || 0) + (currentValue || 0), 0);
                rowData[summaryField] = totalSum;
            } else if (!preview && !this._hideMarks && this._round.judging_type !== JudgingType.CollectStars) {
                rowData[summaryField] = stateSums[componentId]?.s;
            }
        } else if (this._partialResults?.length > 0) {
            let partialSummary = 0;
            const teamId = this._stateData.teams?.[teamIndex];
            const partialTeamResults = this._partialResults.filter(
                result => this._stateData.judges?.includes(result.judge_id) && result.heats_team_id == teamId
            );
            partialTeamResults.forEach(result => {
                if (!result.result_partial) {
                    return;
                }
                const resultData = RoundStateHelper.safeParseJson<Record<string, any>>(result.result_partial);
                if (!resultData) {
                    return;
                }
                const value = resultData[componentId];
                if (typeof value === 'number') {
                    partialSummary += value;
                } else if (Array.isArray(value)) {
                    value.forEach((item: any) => {
                        partialSummary += item?.value || 0;
                    });
                }
            });
            const decimals = RoundStateHelper.getNumberOfDecimals(decimalStep || 0.1);
            rowData[summaryField] = partialSummary ? partialSummary.toFixed(decimals) : '';
        }

        judges.forEach(judge_id => {
            const partialTeam = this._partialResults.find(
                result => result.judge_id == judge_id && result.heats_team_id == this._stateData.teams?.[teamIndex]
            );
            if (!partialTeam?.result_partial) {
                return;
            }
            const resultData = RoundStateHelper.safeParseJson<Record<string, any>>(partialTeam.result_partial);
            if (!resultData) {
                return;
            }
            if (resultData[componentId] !== undefined) {
                rowData[`result_${judge_id}`] = resultData[componentId];
            }
        });

        return rowData;
    }

    buildResultRenderModel(params: RoundStateRenderModelParams = {}): RoundStateRenderModel {
        const summaryField = params.summaryField || this.getSummaryField(!!params.preview);
        const judges = [...(this.getJudges(params.showTrainees) || [])] as string[];
        const components = params.components ?? RoundStateHelper.getDisplayComponents(this._round);
        const canUsePartialData = RoundStateHelper.shouldUsePartialData(this._round, !!params.preview);
        const includePartialRows = params.includePartialRows ?? (canUsePartialData && components.length > 0);
        const filteredCoachTeams = params.filteredCoachTeams ?? this.getFilteredCoachTeams(params.coachFilter);
        if (RoundItem.isPointSummary(this._round) && !judges.includes('multiplier')) {
            judges.push('multiplier');
        }

        const helperTeamList = this.buildTeamList(filteredCoachTeams) || [];
        const rows = helperTeamList
            .map((helperTeam: any, keyIndex: number) => {
                const teamId = helperTeam.id;
                const teamIndex = this._stateData.teams.indexOf(teamId);
                if (teamIndex < 0) {
                    return null;
                }

                const remark = RoundStateHelper.normalizeRemarkState(helperTeam.remarks);
                const placement = helperTeam.placement;
                const startNumber = helperTeam.startNumber || this._stateData.teamNames?.[teamId] || '';
                const fullName = helperTeam.fullName || startNumber;
                const placementMeta = this.getTeamPlacementMeta(teamId, teamIndex);
                const placementLabel = placementMeta.priorityLabel ?? placementMeta.placementLabel ?? '';
                const canShowPlacement = RoundItem.shouldShowPlacement(this._round, placement, teamId as any, filteredCoachTeams as any);
                const canShowPartial = !!(canUsePartialData && (params.preview || (!this._hideMarks && RoundItem.shouldShowPartial(this._round, placement, teamId as any, filteredCoachTeams as any))));

                const row: any = {
                    teamId,
                    key: keyIndex,
                    teamName: startNumber,
                    fullName,
                    remark,
                    cancelled: remark === 'd',
                    sum: helperTeam.sums ?? '',
                    groupSums: helperTeam.groupSums ?? '',
                    componentSums: helperTeam.componentSums ?? this.getTeamComponentSumLabel(teamIndex),
                    position: placement,
                    placementLabel,
                    placementMeta,
                    canShowPlacement,
                    canShowPartial,
                    placement: canShowPlacement ? placementLabel : '',
                    priority: helperTeam.priority ?? this._stateData.priorities?.[teamId],
                    startNumber
                };

                judges.forEach(judge => {
                    switch (judge) {
                        case 'multiplier': {
                            row[`result_${judge}`] = this._stateData.componentSums?.[teamIndex]?.['base']?.m ?? '';
                        } break;
                        case 'partial': {
                            const stateKeys = this._stateData.componentSums?.[teamIndex] && Object.keys(this._stateData.componentSums?.[teamIndex]);
                            if (stateKeys) {
                                const allSums = stateKeys.map((key: string) => this._stateData.componentSums?.[teamIndex][key].s);
                                row[`result_${judge}`] = allSums.reduce((accumulator: number, currentValue: number) => (accumulator || 0) + (currentValue || 0), 0);
                            }
                        } break;
                        default: {
                            row[`result_${judge}`] = this.getJudgeDisplayResult(judge, teamIndex, {
                                preview: params.preview,
                                filteredCoachTeams,
                                decimalStep: params.decimalStep
                            });
                            row[`remarks_${judge}`] = this.getJudgeRemarks(judge, teamIndex, placement, filteredCoachTeams);
                            if (params.includeResultComments) {
                                const parsedComments = this.getJudgeComments(judge, teamId) as { remarks?: any[] } | null;
                                row[`comment_${judge}`] = parsedComments?.remarks || [];
                            }
                        } break;
                    }
                });

                const majorityArray = this._stateData.majority?.[teamIndex];
                if (Array.isArray(majorityArray)) {
                    majorityArray.forEach((majorityValue: any, majorityIndex: number) => {
                        row[`majority_${majorityIndex}`] = majorityValue;
                        row[`count_${majorityIndex}`] = (this._stateData.counts?.[teamIndex] as any)?.[majorityIndex + 1];
                    });
                }

                if (includePartialRows && components.length) {
                    if (canShowPartial) {
                        row.children = components.map(component => {
                            const judgeResults = this.buildComponentJudgeResults({
                                teamIndex,
                                componentId: component.id,
                                judges,
                                preview: params.preview,
                                summaryField,
                                decimalStep: component.step || 0.1
                            });
                            return {
                                teamId: `${teamId}-${component.id}-comp`,
                                componentId: component.id,
                                componentName: component.name,
                                ...judgeResults
                            };
                        });
                    }
                }

                return row;
            })
            .filter((row: any): row is any => !!row);

        return {
            judges,
            rows: this.sortDisplayRows(rows, filteredCoachTeams, params.hideMarks),
            summaryField,
            filteredCoachTeams
        };
    }

    getJudgeRemarks(judge_id: string, teamIndex: number, placement: number, filteredCoachTeams?: string[]) {
        if (judge_id == 'average') {
            return null;
        }

        const team = this._stateData.teams[teamIndex];
        if (!this.hasRoundJudgeIdValue() && (this._hideMarks || !this.shouldShowJudgeMarksValue(placement, team, filteredCoachTeams))) {
            return null;
        }

        if (this._stateData.judgeRemarks) {
            if (this.isTraineeJudgeId(judge_id)) {
                if (this._ratings) {
                    const score = this._ratings.find(result => result.heats_team_id == this._stateData.teams[teamIndex]);
                    if (score) {
                        const result_partial = score.result_partial;
                        if (result_partial) {
                            const result = RoundStateHelper.safeParseJson<{ remarks?: any }>(result_partial);
                            return result ? result.remarks : null;
                        }
                    }
                }
                return null;
            }
            return this._stateData.judgeRemarks[judge_id][teamIndex];
        }
        return null;
    }

    private getRoundJudgeType(): JudgeType | undefined {
        return this._round.judge_type;
    }

    private isTraineeJudgeType(type?: JudgeType): boolean {
        return type === JudgeType.Trainee;
    }

    private hasRoundJudgeIdValue(): boolean {
        return !!this._round.judge_id;
    }

    private hasRoundJudgeIdNumber(): boolean {
        return (this._round.judge_id as any) > 0;
    }

    private hasTraineeJudge(): boolean {
        return !!(this.hasRoundJudgeIdNumber() && this.isTraineeJudgeType(this.getRoundJudgeType()));
    }

    private isTraineeJudgeId(judge_id?: string): boolean {
        return !!(this.hasRoundJudgeIdNumber() && judge_id != null && this._round.judge_id == judge_id && this.isTraineeJudgeType(this.getRoundJudgeType()));
    }

    getTeamSumLabel(teamIndex: number, filteredCoachTeams?: string[]) {
        const team = this._stateData.teams[teamIndex];
        const placement = this.getTeamPlacement(teamIndex);
        if (!this.hasRoundJudgeIdValue() && (this._hideMarks || !this.shouldShowJudgeMarksValue(placement, team, filteredCoachTeams))) {
            if (RoundItem.usesStars(this._round)) {
                return '<i class="glyphicon glyphicon-star"></i>';
            }
            return null;
        }
        return this._stateData.sums ? this._stateData.sums[teamIndex] : 0;
    }

    getTeamComponentSumLabel(teamIndex: number) {
        if (RoundItem.isPointSummary(this._round)) {
            let newSums = this._stateData.componentSums && { ...this._stateData.componentSums[teamIndex] };
            if (newSums) {
                const allSums = Object.keys(newSums).map(key => newSums[key].s);
                const partialSum = allSums.reduce((memo: number, num: number) => memo + num, 0);
                newSums['partial'] = { m: null, s: partialSum };
            } else {
                newSums = { 'partial': { m: null, s: null } };
            }
            return newSums;
        }
        return this._stateData.componentSums ? this._stateData.componentSums[teamIndex] : 0;
    }

    getTeamPlacementLabel(teamIndex: number) {
        const team = this._stateData.teams[teamIndex];
        const targetId = this._stateData.targets ? this._stateData.targets[team] : 0;
        do {
            if (!targetId) {
                break;
            }

            let targetRound = this._classRounds.find(round => round.round_id == targetId);
            if (!targetRound) {
                break;
            }

            if (targetRound.round_type == 'chance') {
                const state = this._states.find(round => round.round_id == targetId);
                if (!state) {
                    break;
                }

                const data = state.state_data as any;
                const teamStartNumber = this._stateData.teamNames[team];
                const values = Object.values(data.teamNames || {});
                const keys = Object.keys(data.teamNames || {});
                const offset = values.indexOf(teamStartNumber);
                if (offset < 0) {
                    break;
                }

                const localId = keys[offset];
                const chanceTarget = data.targets ? data.targets[localId] : null;
                if (!chanceTarget) {
                    break;
                }

                targetRound = this._classRounds.find(round => round.round_id == chanceTarget);
            }

            if (!targetRound) {
                break;
            }

            return RoundItem.getShortLetters(targetRound);
        // eslint-disable-next-line no-constant-condition
        } while (0);

        if ((this._round.round_type == 'chance') || this._round.round_qualifier) {
            return '';
        }

        if(this._stateData.positions[teamIndex] > 0 ) {
            const placement = this._stateData.positions[teamIndex];
            if(RoundItem.shouldShowPlacement(this._round, placement)) {
                return placement;
            }
        }

        return '';
    }

    getTeamPriorityLabel(team: string, teamIndex: number) {
        let priorityLabel: any = '';
        const priority = this._stateData.priorities ? this._stateData.priorities[team] : null;
        const roundPrioritySettings = RoundItem.getPrioritySettings(this._round);
        if (roundPrioritySettings) {
            switch (roundPrioritySettings.type) {
                case 'alternates': {
                    if (priority != null) {
                        if (priority > 0) {
                            priorityLabel = priority;
                        } else {
                            priorityLabel = 'team.priority.alternates.through';
                        }
                    } else {
                        priorityLabel = this.getTeamPlacementLabel(teamIndex);
                    }
                } break;
                case 'groups': {
                    if (priority != null) {
                        priorityLabel = roundPrioritySettings.groups?.[priority]?.name || priority;
                    } else {
                        priorityLabel = this.getTeamPlacementLabel(teamIndex);
                    }
                } break;
                default: {
                    priorityLabel = priority;
                } break;
            }
        }

        return priorityLabel;
    }

    getTeamPlacement(teamIndex: number) {
        if(this._stateData.positions[teamIndex] > 0) {
            return this._stateData.positions[teamIndex];
        }

        return teamIndex;
    }
}
